import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Footer } from '@/components/layouts/Footer';
import { ProductCard, type Product } from '@/components/ui/ProductCard';
import { useSanityContext } from '@/sanity/SanityDataProvider';
import { urlFor } from '@/sanity/client';

// R2 - Full product catalog with images and features
const PRODUCTS: Product[] = [
  {
    id: 1,
    title: 'Módulos Prefabricados',
    description: 'Soluciones modulares de alta calidad para cualquier proyecto constructivo o temporal.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1764269542/modulo_home_new_hlbrx7.jpg',
    category: 'Construcción',
    features: ['6m estándar', 'Aislamiento térmico', 'Instalación rápida'],
  },
  {
    id: 2,
    title: 'Módulos Sanitarios',
    description: 'Aseos portátiles con acabados premium y máxima higiene para obras y eventos.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__2_ujmieo.jpg',
    category: 'Sanitarios',
    features: ['Alta capacidad', 'Fácil limpieza', 'Diseño moderno'],
  },
  {
    id: 3,
    title: 'WC Químico Portátil',
    description: 'Soluciones compactas y eficientes para eventos y obras de cualquier tamaño.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__2_ujmieo.jpg',
    category: 'Eventos',
    features: ['Compacto', 'Autónomo', 'Ecológico'],
  },
  {
    id: 4,
    title: 'Contenedor Marítimo',
    description: 'Venta y alquiler de contenedores para almacenamiento y proyectos especiales.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__1_bvig72.jpg',
    category: 'Almacenamiento',
    features: ['20 y 40 pies', 'Alta resistencia', 'Versatilidad'],
  },
];

/**
 * R2 - Full products catalog page using reusable ProductCard
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function ProductosPage() {
  const { data } = useSanityContext();

  const badge = data?.products?.badge ?? 'Catálogo Completo';
  const pageTitle = data?.products?.title ?? 'Nuestros Productos';
  const pageDesc = data?.products?.description ?? 'Soluciones modulares de primera calidad para cada necesidad';

  // R4 - Resolve all products from Sanity or use hardcoded defaults
  const resolvedProducts: Product[] = data?.products?.items?.length
    ? data.products.items.map((item, idx) => ({
        id: idx + 1,
        title: item.title,
        description: item.description,
        image: urlFor(item.image).width(600).url(),
        category: item.category,
        features: item.features,
      }))
    : PRODUCTS;

  return (
    <>
      <section className="py-14 sm:py-16 bg-gradient-to-b from-white to-inboca-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-inboca-teal hover:text-inboca-teal/80 font-medium mb-8 transition-colors animate-fade-in-up"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al Inicio
          </Link>

          {/* Section Header - Matching FeaturedProductsSection */}
          <div className="text-center mb-16 sm:mb-20 animate-fade-in-up">
            <span className="inline-block px-4 py-2 bg-inboca-teal/10 rounded-full text-inboca-teal font-semibold text-sm mb-6">
              {badge}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {pageTitle}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {pageDesc}
            </p>
          </div>

          {/* Products Grid - Same design as FeaturedProductsSection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {resolvedProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center animate-fade-in-up">
            <div className="inline-flex flex-col items-center gap-6 p-8 bg-white rounded-3xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900">
                ¿No encuentras lo que buscas?
              </h2>
              <p className="text-lg text-gray-700">
                Ofrecemos soluciones personalizadas para proyectos especiales
              </p>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-3 bg-inboca-teal hover:bg-inboca-teal/90 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Contáctanos
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer data={data?.footer} contactInfo={data?.contactInfo} />
    </>
  );
}
